import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { SvgIconHandler } from "../../atoms/svg-icon-hadler";
import { MONTHS, START_YEAR, YEARS } from "../../../constants";
import "./date-filter.m.css";

// Get default date filter (Jan 2023 to current month/year)
const getDefaultDateFilter = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  return {
    startMonth: "1",
    startYear: "2023",
    endMonth: currentMonth.toString(),
    endYear: currentYear.toString()
  };
};

const DateFilter = ({ 
  isVisible, 
  onClose, 
  onApply, 
  initialDateFilter,
}) => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [activeSelector, setActiveSelector] = useState('');
  const [dateFilter, setDateFilter] = useState(initialDateFilter || getDefaultDateFilter());

  // Update dateFilter when initialDateFilter prop changes
  useEffect(() => {
    if (initialDateFilter) {
      setDateFilter(initialDateFilter);
    }
  }, [initialDateFilter]);

  // Check if mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);


  const handleDateFilterChange = (field, value) => {
    setDateFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApply = () => {
    onApply(dateFilter);
    onClose();
  };

  const handleClear = () => {
    const clearedFilter = getDefaultDateFilter();
    setDateFilter(clearedFilter);
  };

  const handleMonthClick = (monthValue) => {
    const disabled = (activeSelector === 'startMonth' ? isStartMonthDisabled(monthValue) : isEndMonthDisabled(monthValue));
    if (disabled) return;
    handleDateFilterChange(activeSelector, monthValue);
  };

  const handleYearClick = (year) => {
    const disabled = (activeSelector === 'startYear' ? isStartYearDisabled(year) : isEndYearDisabled(year));
    if (disabled) return;
    const newValue = year.toString();
    handleDateFilterChange(activeSelector, newValue);
  };

  // Date bounds
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12

  const isStartYearDisabled = (year) => {
    if (year < START_YEAR) return true;
    if (year > currentYear) return true;
    return false;
  };

  const isEndYearDisabled = (year) => {
    if (year < START_YEAR) return true;
    if (year > currentYear) return true;
    if (dateFilter.startYear) {
      const startYearNum = parseInt(dateFilter.startYear, 10);
      if (year < startYearNum) return true;
    }
    return false;
  };

  const isStartMonthDisabled = (monthValue) => {
    const monthNum = parseInt(monthValue, 10);
    const startYearNum = parseInt(dateFilter.startYear || "", 10);
    if (!isNaN(startYearNum)) {
      if (startYearNum === currentYear && monthNum > currentMonth) return true;
      // Lower bound is Jan 2023 which is always month 1; no extra block needed
    }
    return false;
  };

  const isEndMonthDisabled = (monthValue) => {
    const monthNum = parseInt(monthValue, 10);
    const endYearNum = parseInt(dateFilter.endYear || "", 10);
    const startYearNum = parseInt(dateFilter.startYear || "", 10);
    const startMonthNum = parseInt(dateFilter.startMonth || "", 10);
    // Disallow future months in current year
    if (!isNaN(endYearNum) && endYearNum === currentYear && monthNum > currentMonth) return true;
    // Disallow months earlier than start month when same year
    if (!isNaN(endYearNum) && !isNaN(startYearNum) && endYearNum === startYearNum && !isNaN(startMonthNum) && monthNum < startMonthNum) return true;
    return false;
  };

  const isDateRangeValid = () => {
    const { startMonth, startYear, endMonth, endYear } = dateFilter;
    if (!startMonth || !startYear || !endMonth || !endYear) {
      return true;
    }
    const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1);
    const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1);
    return startDate <= endDate;
  };

  if (!isVisible) return null;

  const getMonthLabel = (monthValue) => {
    const month = MONTHS.find(m => m.value === monthValue);
    return month ? month.label : '';
  };


  return (
    <div>
        <div styleName="date-filter-wrapper" onClick={() => onClose(true)} />
        <div styleName="date-filter-popup">
          <div styleName="date-filter-header">
          <div styleName="filter-header-wrapper">
            <h3 styleName="date-filter-title">Showing reports from</h3>
            <button onClick={() => onClose(true)} styleName="date-filter-close">
              <SvgIconHandler type="close" width="12" height="12" />
            </button>
          </ div>
          <>
            <div styleName="date-range-display">
              <div styleName="date-box clickable" onClick={() => setActiveSelector('startMonth')}>
                <span styleName={`date-value ${activeSelector === "startMonth" ? "active-selector" : ""}`}>{getMonthLabel(dateFilter.startMonth)}</span>
              </div>
              <div styleName="date-box clickable" onClick={() => setActiveSelector('startYear')}>
                <span styleName={`date-value ${activeSelector === "startYear" ? "active-selector" : ""}`}>{dateFilter.startYear}</span>
              </div>
              <span styleName="date-separator">to</span>
              <div styleName="date-box clickable" onClick={() => setActiveSelector('endMonth')}>
                <span styleName={`date-value ${activeSelector === "endMonth" ? "active-selector" : ""}`}>{getMonthLabel(dateFilter.endMonth)}</span>
              </div>
              <div styleName="date-box clickable" onClick={() => setActiveSelector('endYear')}>
                <span styleName={`date-value ${activeSelector === "endYear" ? "active-selector" : ""}`}>{dateFilter.endYear}</span>
              </div>
            </div>
          </>
          </div>
          <div styleName={`month-selector ${!activeSelector ? "hide" : ""} ${(activeSelector === 'startMonth' || activeSelector === 'endMonth') ? 'month-selector' : 'year-selector'}`}>
            {(activeSelector === 'startMonth' || activeSelector === 'endMonth') ? 
              MONTHS.map(month => {
                const isSelected = dateFilter[activeSelector] === month.value;
                const isDisabled = activeSelector === 'startMonth' 
                  ? isStartMonthDisabled(month.value) 
                  : isEndMonthDisabled(month.value);
                const classNames = ['month-button', isSelected && 'selected', isDisabled && 'disabled']
                  .filter(Boolean)
                  .join(' ');
                
                return (
                  <button
                    key={month.value}
                    styleName={classNames}
                    disabled={isDisabled}
                    onClick={() => handleMonthClick(month.value)}
                  >
                    {month.label}
                  </button>
                );
              }) :
              YEARS.map(year => {
                const isSelected = dateFilter[activeSelector] === year.toString();
                const isDisabled = activeSelector === 'startYear' 
                  ? isStartYearDisabled(year) 
                  : isEndYearDisabled(year);
                const classNames = ['month-button', isSelected && 'selected', isDisabled && 'disabled']
                  .filter(Boolean)
                  .join(' ');
                
                return (
                  <button
                    key={year}
                    styleName={classNames}
                    disabled={isDisabled}
                    onClick={() => handleYearClick(year)}
                  >
                    {year}
                  </button>
                );
              })
            }
          </div>

          {
            !isDateRangeValid() && (
              <div styleName="date-filter-error mobile">Please select a valid date range.</div>
            )
          }
          
          <div styleName={`date-filter-actions ${!activeSelector ? "hide" : ""}`}>
          {
            !isDateRangeValid() && (
              <div styleName="date-filter-error desktop">Please select a valid date range.</div>
            )
          }
          <button 
            styleName="date-filter-clear" 
            onClick={handleClear}
          >
            Reset
          </button>
          <button 
            styleName="date-filter-apply" 
            onClick={handleApply}
            disabled={!isDateRangeValid()}
          >
            Apply
          </button>
          </div>
        </div>
    </div>
  );
};

DateFilter.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  initialDateFilter: PropTypes.shape({
    startMonth: PropTypes.string,
    startYear: PropTypes.string,
    endMonth: PropTypes.string,
    endYear: PropTypes.string
  })
};

export { DateFilter };
